// Deadline/Task Form (Work Tab)
import React, { useState } from 'react';
import ResponsiveModal from './ResponsiveModal';
import { Target, Plus } from 'lucide-react';

interface DeadlineFormProps {
  onAdd: (item: { title: string; dueDate?: string; notes?: string; metadata?: any }) => void;
}

export default function DeadlineForm({ onAdd }: DeadlineFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [listName, setListName] = useState('');
  const [taskName, setTaskName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<'high' | 'medium' | 'low'>('medium');
  const [status, setStatus] = useState<'todo' | 'inprogress' | 'done'>('todo');
  const [notes, setNotes] = useState('');  

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!listName.trim()) {
      alert('Please name your deadline group!');
      return;
    }
    if (!taskName.trim()) return;

    const priorityEmoji = {
      high: '🔴',
      medium: '🟡',
      low: '🟢'
    };

    const statusEmoji = {
      todo: '📋',
      inprogress: '⚙️',
      done: '✅'
    };

    onAdd({
      title: `${priorityEmoji[priority]} ${statusEmoji[status]} ${taskName.trim()}`,
      dueDate: dueDate || undefined,
      notes: notes || undefined,
      metadata: {
        type: 'deadline',
        listName: listName.trim(),
        priority,
        status
      }
    });

    // Reset form
    setListName('');
    setTaskName('');
    setDueDate('');
    setPriority('medium');
    setStatus('todo');
    setNotes('');
    setIsOpen(false);
  };

  return (
    <>
      {/* Add Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="w-full md:w-auto px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation"
      >
        <Target className="w-5 h-5" />
        Add Deadline
      </button>

      {/* Form Modal */}
      <ResponsiveModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Add Deadline/Task"
        maxWidth="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Deadline Group Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">
              Deadline Group Name *
            </label>
            <input
              type="text"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder="e.g., Q1 Deadlines, Project Milestones, Personal Goals"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 text-base"
              required
              autoFocus
            />
            <p className="text-xs text-slate-500 mt-1">Name this deadline group to organize related tasks</p>
          </div>

          {/* Task Name */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Task Name *
            </label>
            <input
              type="text"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 text-base"
              placeholder="e.g., Finish Q4 Report, Submit Proposal"
              required
              autoFocus
            />
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Due Date
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
            />
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Priority *
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setPriority('high')}
                className={`py-3 px-4 rounded-lg font-semibold transition-colors touch-manipulation ${
                  priority === 'high'
                    ? 'bg-red-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                🔴 High
              </button>
              <button
                type="button"
                onClick={() => setPriority('medium')}
                className={`py-3 px-4 rounded-lg font-semibold transition-colors touch-manipulation ${
                  priority === 'medium'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                🟡 Medium
              </button>
              <button
                type="button"
                onClick={() => setPriority('low')}
                className={`py-3 px-4 rounded-lg font-semibold transition-colors touch-manipulation ${
                  priority === 'low'
                    ? 'bg-green-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                🟢 Low
              </button>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Status *
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setStatus('todo')}
                className={`py-3 px-4 rounded-lg font-semibold transition-colors touch-manipulation ${
                  status === 'todo'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                📋 To Do
              </button>
              <button
                type="button"
                onClick={() => setStatus('inprogress')}
                className={`py-3 px-4 rounded-lg font-semibold transition-colors touch-manipulation ${
                  status === 'inprogress'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                ⚙️ In Progress
              </button>
              <button
                type="button"
                onClick={() => setStatus('done')}
                className={`py-3 px-4 rounded-lg font-semibold transition-colors touch-manipulation ${
                  status === 'done'
                    ? 'bg-green-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                ✅ Done
              </button>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base resize-none"
              placeholder="Additional details..."
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full py-4 px-6 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation text-lg md:text-base mt-6"
          >
            <Plus className="w-5 h-5" />
            Add Deadline
          </button>
        </form>
      </ResponsiveModal>
    </>
  );
}
